import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import { getAppInsights } from '../../../Services/Global';

/**
 * Props for FeedbackForm component
 */
export interface FeedbackFormProps {
  /**
   * The question text to display above the rating buttons
   */
  questionText?: string;
  /**
   * Callback function called when the form is submitted
   */
//   onSubmit?: (rating: number, feedback: string) => void;
  /**
   * Whether the form is in a loading state
   */
  isLoading?: boolean;
  /**
   * Custom styles for the component
   */
  sx?: SxProps<Theme>;
}

/**
 * FeedbackForm component - Material UI version
 * 
 * A feedback form component with rating buttons and text input
 *
 * @param {FeedbackFormProps} props Component properties
 * @return {React.ReactElement} The FeedbackForm component
 */
export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  questionText = "How do you rate AI Design Wizard?",
//   onSubmit,
  isLoading = false,
  sx
}) => {
  const theme = useTheme();
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const [isTextTooLong, setIsTextTooLong] = useState(false);
  const [lines, setLines] = useState(0);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);
  const appInsights = getAppInsights();

  // Rating button configurations
  const ratingButtons = [
    { value: 1, icon: SentimentVeryDissatisfiedIcon, label: 'Very Difficult', color: '#f44336' },
    { value: 2, icon: SentimentDissatisfiedIcon, label: 'Difficult', color: '#ff9800' },
    { value: 3, icon: SentimentNeutralIcon, label: 'Neutral', color: '#ffc107' },
    { value: 4, icon: SentimentSatisfiedIcon, label: 'Easy', color: '#4caf50' },
    { value: 5, icon: SentimentVerySatisfiedIcon, label: 'Super Easy', color: '#2196f3' }
  ];

  // Handle rating selection
  const handleRatingClick = useCallback((selectedRating: number) => {
    setRating(selectedRating);

    appInsights?.trackEvent({ name: 'useraction:FeedbackRatingSelected', properties: { 'timestamp': new Date().toISOString(), 'rating': selectedRating } });
  }, []);

  // Handle feedback text change
  const handleFeedbackChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFeedback(newValue);
  }, []);

  // Update text length state whenever feedback changes
  useEffect(() => {
    const isLong = feedback.length > 20;
    setIsTextTooLong(isLong);
    setLines(Math.floor(feedback.length / 33))
  }, [feedback]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setRating(null);
    setFeedback('');
    setIsTextTooLong(false);
    setLines(0);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if ( rating !== null ) {
    // do the submit to the api
    // onSubmit(rating, feedback);
    
    appInsights?.trackEvent({ name: 'useraction:FeedbackSubmitted', properties: { 'timestamp': new Date().toISOString(), 'rating': rating, 'feedback': feedback } });
    }
    // Show thank you dialog
    setShowThankYouDialog(true);
    
    // Auto-close dialog after 5 seconds
    setTimeout(() => {
      setShowThankYouDialog(false);
      resetForm(); // Reset form when auto-closing
    }, 5000);

  }, [rating, feedback, resetForm]);

  // Component styles
  const styles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // width: 'min-content',
    width: 450, // Half the original width
    padding: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1,
    boxShadow: 1,
    ...sx
  };

  const questionStyles: SxProps<Theme> = {
    textAlign: 'center',
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: '0.9rem',
    // lineHeight: 0.75
  };

  const ratingContainerStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
    width: '100%'
  };

  const ratingButtonStyles = (isSelected: boolean, color: string): SxProps<Theme> => ({
    minWidth: 'auto',
    padding: 1,
    borderRadius: '50%',
    backgroundColor: isSelected ? color : 'transparent',
    color: isSelected ? 'white' : theme.palette.text.secondary,
    border: `2px solid ${isSelected ? color : theme.palette.divider}`,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: isSelected ? color : theme.palette.action.hover,
      transform: 'scale(1.1)'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
    }
  });

  const textFieldStyles: SxProps<Theme> = {
    width: '100%',
    fontSize: '0.9rem',
    '& .MuiOutlinedInput-root': {
      transition: 'all 0.2s ease-in-out',
    }
  };

  const submitButtonStyles: SxProps<Theme> = {
    padding: 1.5,
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    // minWidth: 'fit-content',
    // height: '40px' // Fixed height to match text field
  };

  const dialogStyles: SxProps<Theme> = {
    '& .MuiDialog-paper': {
      borderRadius: 2,
      padding: 1
    }
  };

  return (
    <Box sx={styles}>
      {/* First row: Question text */}
      <Typography variant="body1" sx={questionStyles}>
        {questionText}
      </Typography>

      {/* Second row: Rating buttons */}
      <Box sx={ratingContainerStyles}>
        {ratingButtons.map(({ value, icon: Icon, label, color }) => (
          <Tooltip key={value} title={label} arrow>
            <IconButton
              onClick={() => handleRatingClick(value)}
              sx={ratingButtonStyles(rating === value, color)}
              aria-label={label}
              disabled={isLoading}
            >
              <Icon sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      {/* Third row: Feedback text input and submit button - only shown when rating is selected */}
      {rating !== null && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'flex-start',
          flexDirection: isTextTooLong ? 'column' : 'row',
          width: '100%',
          position: 'relative'
        }}>
<Tooltip title={"Is there anything we can improve? (optional)"} arrow>
          <TextField
            ref={textFieldRef}
            label="Is there anything we can improve? (optional)"
            multiline
            rows={lines ? (lines + 1) : 1}
            value={feedback}
            onChange={handleFeedbackChange}
            placeholder="Share your thoughts..."
            disabled={isLoading}
            sx={{
              ...textFieldStyles,
              flex: isTextTooLong ? 'none' : 1,
              width: isTextTooLong ? '100%' : 'auto',
              minWidth: 0, // Allow text field to shrink
              flexShrink: 1,
              '& .MuiInputBase-root': {
                height: isTextTooLong ? 'unset' : '40px', // Match button height
                lineHeight: '0.9em',
              },
              '& .MuiFormLabel-root': {
                '&:not(.Mui-focused)': {
                    fontSize: feedback.length > 0 ? '1rem' : '0.7rem',
                }
            }
            }}
            inputProps={{
              maxLength: 1000
            }}
          />
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              ...submitButtonStyles,
              width: isTextTooLong ? '100%' : 'auto',
              flexShrink: 0, // Prevent button from shrinking,
              padding: "6px 16px", 
            }}
          >
            Send
          </Button>
        </Box>
      )}

      {/* Thank You Dialog */}
      <Dialog
        open={showThankYouDialog}
        onClose={() => setShowThankYouDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={dialogStyles}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          Thank You! 🙏
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Thank you for your valuable feedback! Your input helps us continuously improve our product and provide a better experience for all users.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We're committed to making our platform even better based on feedback like yours.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowThankYouDialog(false);
              resetForm(); // Reset form when manually closing
            }}
            sx={{ minWidth: 120 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
